import { useFlags } from 'launchdarkly-react-client-sdk';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { partnerApi } from '../lib/api';
import { LaunchDarklyFlags } from '../types/launch-darkly';

interface PostHogProviderWrapperProps {
  children: ReactNode;
  debug?: boolean;
}

export const PostHogProviderWrapper = ({ children, debug = false }: PostHogProviderWrapperProps) => {
  const isProd = import.meta.env.PROD;
  const flags = useFlags<LaunchDarklyFlags>();
  const canRecord = flags?.['enable-posthog-recording'] ?? false;
  const [identified, setIdentified] = useState<boolean>(false);
  const { isAuthenticated, user, organization } = useAuth();

  // Fetch partner data to get partner ID
  const { data: partner } = useQuery({
    queryKey: ['partner'],
    queryFn: () => partnerApi.getPartner(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: isAuthenticated,
  });

  // Track if we've already identified the user to prevent re-identification
  const hasIdentified = useRef(false);
  const currentIdentityKey = useRef<string | null>(null);
  const personPropertiesSet = useRef(false);

  useEffect(() => {
    // We wait for partner data before identifying the user.
    if (!partner?.id || !isAuthenticated) {
      return;
    }

    const partnerId = partner.id;
    // Use partner ID as the identity key since there's no tenant ID
    const identityKey = partnerId;

    // Only identify if we haven't already identified with this exact identity
    // This prevents re-identification when just the session ID changes
    if (hasIdentified.current && currentIdentityKey.current === identityKey) {
      if (debug) {
        console.log('PostHog identity already set, skipping re-identification');
      }
      return;
    }

    if (debug) {
      console.log('PostHog Initialization:', {
        partnerId,
        userId: user?.id,
        organizationId: organization?.id,
        isReidentification: hasIdentified.current,
      });
    }

    // Identify the user with partner ID and user/organization info
    const identifyProperties: Record<string, unknown> = {
      partner_id: partnerId,
    };

    if (user?.id) {
      identifyProperties.user_id = user.id;
      identifyProperties.user_email = user.email;
    }

    if (organization?.id) {
      identifyProperties.organization_id = organization.id;
      identifyProperties.organization_name = organization.name;
    }

    posthog.identify(identityKey, identifyProperties);

    hasIdentified.current = true;
    setIdentified(true);
    if (currentIdentityKey.current !== identityKey) {
      // Reset enrichment tracking if identity changed
      personPropertiesSet.current = false;
    }
    currentIdentityKey.current = identityKey;
  }, [
    partner?.id,
    partner?.name,
    user?.id,
    user?.email,
    organization?.id,
    organization?.name,
    debug,
    isAuthenticated,
  ]);

  // Enrich person profile with partner name once available
  useEffect(() => {
    if (!identified) {
      return;
    }
    if (personPropertiesSet.current) {
      return;
    }
    if (!partner?.name) {
      return;
    }

    const setProps = {
      partner_name: partner.name,
    } as Record<string, unknown>;

    try {
      const ph = posthog as unknown as {
        setPersonProperties?: (set: Record<string, unknown>, setOnce?: Record<string, unknown>) => void;
      };
      if (typeof ph.setPersonProperties === 'function') {
        ph.setPersonProperties(setProps, {});
      } else {
        posthog.capture('$set', { $set: setProps });
      }
      personPropertiesSet.current = true;
    } catch (e) {
      console.error('Failed to set PostHog person properties', e);
    }
  }, [identified, partner?.name, partner?.id, debug]);

  // Effect for controlling session recording based on the feature flag
  useEffect(() => {
    if (isProd && canRecord && partner && !partner.hipaaCompliant) {
      posthog.startSessionRecording();

      return () => {
        posthog.stopSessionRecording();
      };
    }
  }, [canRecord, isProd, partner]);

  // Cleanup only on unmount
  useEffect(() => {
    return () => {
      posthog.reset();
      hasIdentified.current = false;
      currentIdentityKey.current = null;
    };
  }, [debug]);

  // In non-production environments, do not initialize PostHog at all to avoid polluting data
  if (!isProd) {
    return <>{children}</>;
  }

  return (
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_PARTNER_DASHBOARD_POSTHOG_KEY}
      options={{
        api_host: import.meta.env.VITE_PUBLIC_PARTNER_DASHBOARD_POSTHOG_HOST,
        capture_exceptions: isProd,
        debug,
        person_profiles: 'identified_only',
        disable_session_recording: true,
      }}>
      {children}
    </PostHogProvider>
  );
};
